/**
 * File System Access 实现（Chromium）。见 types.ts 的总注释。
 *
 * 原生 API 经由 {@link FsaPickers} 注入：一来测试能用假实现驱动全部路径，
 * 二来探测逻辑（selectGateway）与使用逻辑解耦。
 */
import {
  isAbortError,
  throwIfAborted,
  type FileGateway,
  type FileHandleRef,
  type GatewayCallOptions,
  type OpenedFile,
  type SavePayload,
} from './types';

/** 原生 FileSystemFileHandle 的最小面，够用且可假。 */
export interface FsaFileHandleLike {
  readonly name: string;
  getFile(): Promise<{ arrayBuffer(): Promise<ArrayBuffer> }>;
  createWritable(): Promise<{
    write(data: BufferSource): Promise<void>;
    close(): Promise<void>;
  }>;
}

interface FsaPickerFileType {
  readonly description?: string;
  readonly accept: Readonly<Record<string, readonly string[]>>;
}

export interface FsaPickers {
  showOpenFilePicker(options?: {
    multiple?: boolean;
    types?: readonly FsaPickerFileType[];
  }): Promise<FsaFileHandleLike[]>;
  showSaveFilePicker(options?: {
    suggestedName?: string;
    types?: readonly FsaPickerFileType[];
  }): Promise<FsaFileHandleLike>;
}

/** 扩展名列表 → picker 的 types 选项。空列表就不限制。 */
function pickerTypes(accept: readonly string[]): readonly FsaPickerFileType[] | undefined {
  if (accept.length === 0) return undefined;
  return [{ accept: { 'application/octet-stream': accept } }];
}

export class FsaGateway implements FileGateway {
  readonly kind = 'fsa' as const;
  readonly canOverwrite = true;

  /**
   * ref → 原生句柄。用 WeakMap 而不是把句柄挂在 ref 上，
   * 是为了让 {@link FileHandleRef} 保持可结构化克隆的纯数据形态。
   */
  readonly #native = new WeakMap<FileHandleRef, FsaFileHandleLike>();

  constructor(private readonly pickers: FsaPickers) {}

  #ref(handle: FsaFileHandleLike): FileHandleRef {
    const ref: FileHandleRef = { gateway: 'fsa', name: handle.name };
    this.#native.set(ref, handle);
    return ref;
  }

  async open(accept: readonly string[], options?: GatewayCallOptions): Promise<OpenedFile[]> {
    throwIfAborted(options?.signal);
    const types = pickerTypes(accept);
    let handles: FsaFileHandleLike[];
    try {
      handles = await this.pickers.showOpenFilePicker(
        types === undefined ? { multiple: true } : { multiple: true, types },
      );
    } catch (error) {
      if (isAbortError(error)) return []; // 用户关掉了选择框
      throw error;
    }

    const opened: OpenedFile[] = [];
    for (const handle of handles) {
      throwIfAborted(options?.signal);
      const file = await handle.getFile();
      opened.push({
        name: handle.name,
        bytes: new Uint8Array(await file.arrayBuffer()),
        handle: this.#ref(handle),
      });
    }
    return opened;
  }

  async save(
    payload: SavePayload,
    handle?: FileHandleRef | null,
    options?: GatewayCallOptions,
  ): Promise<FileHandleRef | null> {
    throwIfAborted(options?.signal);

    // 有句柄 → 原地覆盖（Ctrl-S），不再询问位置
    if (handle != null) {
      const known = this.#native.get(handle);
      if (known !== undefined) {
        await this.#write(known, payload.bytes, options?.signal);
        return handle;
      }
      // 句柄失效（比如跨会话反序列化来的 ref）：落到「另存为」而不是报错
    }

    let target: FsaFileHandleLike;
    try {
      target = await this.pickers.showSaveFilePicker({ suggestedName: payload.name });
    } catch (error) {
      if (isAbortError(error)) return null; // 取消保存是正常路径
      throw error;
    }
    await this.#write(target, payload.bytes, options?.signal);
    return this.#ref(target);
  }

  async #write(
    handle: FsaFileHandleLike,
    bytes: Uint8Array,
    signal: AbortSignal | undefined,
  ): Promise<void> {
    throwIfAborted(signal);
    // createWritable 写的是临时文件，close 时原子替换——中途崩溃不会留半个文件
    const writable = await handle.createWritable();
    await writable.write(bytes as BufferSource);
    await writable.close();
  }
}
